import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { mediaUrl } from "@/lib/api";
import { Surface, Pill, AButton, Skeleton } from "@/components/ios";
import CameraCapture from "@/components/CameraCapture";
import WalkthroughCapture from "@/components/WalkthroughCapture";
import GuidedInspection from "@/components/GuidedInspection";
import { RoomOverview, RoomEvidencePool, ItemCard, CustomItemCard } from "@/components/InspectionPieces";
import { useDictation } from "@/hooks/useDictation";
import { analyzeImageQuality } from "@/lib/imageQuality";
import { compressImage } from "@/lib/image";
import {
  ChevronLeft, Camera, Check, X, Minus, Sparkles, Wifi, WifiOff, Cloud, Mic, MicOff,
  Loader2, Sparkle, ShieldCheck, CheckCheck, ImagePlus, AlertTriangle, ClipboardCheck, Footprints, Plus, Trash2, Aperture, MapPin,
} from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const RATING_LABEL = { clean: "Clean", undamaged: "Undamaged", working: "Working" };

function TriToggle({ field, value, onChange }) {
  const next = value === undefined ? true : value === true ? false : undefined;
  const bg = value === true ? "var(--success)" : value === false ? "var(--danger)" : "var(--surface-2)";
  const color = value === undefined ? "var(--text-secondary)" : "#fff";
  const Icon = value === true ? Check : value === false ? X : Minus;
  return (
    <button
      data-testid={`rating-${field}`}
      onClick={() => onChange(next)}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border"
      style={{ background: bg, color, borderColor: value === undefined ? "var(--border)" : bg }}
    >
      <Icon size={13} /> {RATING_LABEL[field]}
    </button>
  );
}

const thumbSrc = (m) => m.data_url || mediaUrl(m.signed_url);

export default function InspectionCapture() {
  const { id } = useParams();
  const nav = useNavigate();
  const [insp, setInsp] = useState(null);
  const [responses, setResponses] = useState({});
  const [media, setMedia] = useState([]);
  const [transcript, setTranscript] = useState("");
  const [activeRoom, setActiveRoom] = useState(0);
  const [online, setOnline] = useState(true);
  const [pending, setPending] = useState(0);
  const [piiScan, setPiiScan] = useState(false);
  const [gen, setGen] = useState(false);
  const [genStep, setGenStep] = useState(0);
  const [cam, setCam] = useState(null);
  const [walk, setWalk] = useState(false);
  const [guided, setGuided] = useState(true);
  const fileRef = useRef(null);
  const uploadTarget = useRef(null);
  const dict = useDictation();
  const [onsite, setOnsite] = useState(null);
  const geoAsked = useRef(false);

  useEffect(() => {
    api.get(`/inspections/${id}`).then(({ data }) => {
      setInsp(data);
      setResponses(data.responses || {});
      setMedia(data.media || []);
      setTranscript(data.transcript || "");
      setOnsite(data.onsite || null);
    });
  }, [id]);

  // On-site verification: compare device GPS to the property address (once per load).
  useEffect(() => {
    if (!insp || geoAsked.current || !navigator.geolocation) return;
    geoAsked.current = true;
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { data } = await api.post(`/inspections/${id}/verify-location`, {
            lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy,
          });
          setOnsite(data);
          toast[data.verified ? "success" : "message"](
            data.verified ? `On-site verified (${data.distance_m}m from address)` : `Location ${data.distance_m}m from address — not verified`,
            { duration: 2600 });
        } catch (e) {}
      },
      () => {}, { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 },
    );
  }, [insp, id]);

  const setItem = (key, room, patch, quiet) => {
    setResponses((r) => ({ ...r, [key]: { ...(r[key] || {}), room, ...patch } }));
    if (!quiet) {
      setPending((p) => p + 1);
      setTimeout(() => setPending((p) => Math.max(0, p - 1)), 700);
    }
  };

  const save = async (silent) => {
    await api.put(`/inspections/${id}/responses`, { responses, transcript });
    if (!silent) toast.success("Progress saved & synced");
  };

  const markGood = (sections) => {
    setResponses((r) => {
      const nr = { ...r };
      sections.forEach((s) =>
        s.items.forEach((it) => {
          const cur = nr[it.key] || {};
          const patch = {};
          it.rating_fields.forEach((f) => { patch[f] = cur[f] === false ? false : true; });
          nr[it.key] = { ...cur, room: s.title, ...patch };
        })
      );
      return nr;
    });
    setPending((p) => p + 1);
    setTimeout(() => setPending((p) => Math.max(0, p - 1)), 700);
  };

  const applyAnalysis = (itemKey, room, analysis) => {
    if (!analysis) return;
    if (["photo_unusable", "analysis_failed"].includes(analysis.issue_type)) return; // don't false-flag
    setResponses((r) => {
      const cur = r[itemKey] || {};
      const patch = { room };
      const c = analysis.condition || {};
      ["clean", "undamaged", "working"].forEach((f) => {
        if (cur[f] === undefined && c[f] !== undefined) patch[f] = c[f];
      });
      if (!cur.note && analysis.description) { patch.note = analysis.description; patch.ai_generated = true; }
      return { ...r, [itemKey]: { ...cur, ...patch } };
    });
  };

  // Re-generate an item's AI comment across ALL its photos (updates as photos are added/removed).
  const analyzeItem = async (key, room) => {
    if (!key) return;
    try {
      const { data } = await api.post(`/inspections/${id}/items/${encodeURIComponent(key)}/analyze-item`, { room: room || "" });
      if (!data || !data.photo_count) return;
      setResponses((r) => {
        const cur = r[key] || {};
        const patch = { room: room || cur.room };
        const c = data.condition || {};
        ["clean", "undamaged", "working"].forEach((f) => { if (cur[f] === undefined && c[f] !== undefined) patch[f] = c[f]; });
        // Regenerate the AI comment from all photos — unless the agent hand-edited it.
        if ((cur.ai_generated || !cur.note) && data.description) { patch.note = data.description; patch.ai_generated = true; }
        return { ...r, [key]: { ...cur, ...patch } };
      });
    } catch (e) {}
  };

  const uploadPhoto = async (key, room, dataUrl) => {
    try {
      const { data } = await api.post("/media", { inspection_id: id, data_url: dataUrl, room, item_key: key, scan_pii: piiScan });
      setMedia((ms) => [...ms, { ...data, room, item_key: key, data_url: dataUrl }]);
      if (piiScan && data.pii?.redacted) toast.success("Sensitive content detected & blurred");
      else toast.success(`Photo captured · assessing…`, { duration: 1200 });
      analyzeItem(key, room); // combined analysis across ALL photos on this item
    } catch (e) { toast.error("Upload failed"); }
  };

  const onPickFile = (key, room) => { uploadTarget.current = { key, room }; fileRef.current?.click(); };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = await compressImage(reader.result);
      const { key, room } = uploadTarget.current;
      const q = await analyzeImageQuality(dataUrl);
      if (!q.ok) {
        const issues = [q.tooDark && "low light", q.tooBright && "over-exposed", q.blurry && "blurry"].filter(Boolean).join(", ");
        toast.warning(`Photo may be ${issues} — retake if possible`);
      }
      uploadPhoto(key, room, dataUrl);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const assignMedia = async (mid, itemKey) => {
    if (!itemKey) return;
    try {
      await api.post(`/media/${mid}/assign`, { item_key: itemKey });
      setMedia((ms) => ms.map((m) => (m.id === mid ? { ...m, item_key: itemKey, auto_assigned: false } : m)));
      toast.success("Photo attached · assessing…", { duration: 1200 });
      analyzeItem(itemKey, "");
    } catch (e) { toast.error("Could not attach photo"); }
  };

  const unassignMedia = async (mid) => {
    const prevKey = media.find((m) => m.id === mid)?.item_key;
    try {
      await api.post(`/media/${mid}/assign`, { item_key: null });
      setMedia((ms) => ms.map((m) => (m.id === mid ? { ...m, item_key: null, auto_assigned: false } : m)));
      if (prevKey) analyzeItem(prevKey, ""); // re-assess the item now that a photo was removed
    } catch (e) { toast.error("Could not detach photo"); }
  };

  const patchMedia = (mid, patch) => setMedia((ms) => ms.map((m) => (m.id === mid ? { ...m, ...patch } : m)));

  const [matching, setMatching] = useState(false);
  const autoMatch = async () => {
    setMatching(true);
    try {
      const { data } = await api.post(`/inspections/${id}/classify-pending`);
      setMedia((ms) => ms.map((m) => {
        const a = data.assignments.find((x) => x.id === m.id);
        if (!a) return m;
        if (a.item_key) return { ...m, item_key: a.item_key, auto_assigned: true, ai_item_confidence: a.confidence, ai_item_reason: a.reason };
        if (a.ai_candidate) return { ...m, ai_candidate: a.ai_candidate, ai_analysis: a.analysis };
        return m;
      }));
      // Auto-fill condition + draft note for freshly matched items.
      data.assignments.forEach((a) => {
        if (a.item_key && a.analysis) {
          const m = media.find((x) => x.id === a.id);
          applyAnalysis(a.item_key, m?.room || "", a.analysis);
        }
      });
      const matched = data.assignments.filter((a) => a.item_key).length;
      const candidates = data.assignments.filter((a) => a.ai_candidate).length;
      toast.success(
        matched || candidates
          ? `AI matched ${matched} · ${candidates} to confirm`
          : "No confident matches — assign manually"
      );
    } catch (e) { toast.error("Auto-match unavailable"); }
    setMatching(false);
  };

  const acceptCandidate = async (m) => {
    const c = m.ai_candidate;
    if (!c?.item_key) return;
    try {
      await api.post(`/media/${m.id}/assign`, { item_key: c.item_key });
      setMedia((ms) => ms.map((x) => (x.id === m.id ? { ...x, item_key: c.item_key, ai_candidate: null, auto_assigned: false } : x)));
      if (m.ai_analysis) applyAnalysis(c.item_key, m.room || "", m.ai_analysis);
      toast.success(`Confirmed → ${c.label}`);
      analyzeItem(c.item_key, m.room || "");
    } catch (e) { toast.error("Could not confirm"); }
  };

  const dismissCandidate = async (m) => {
    setMedia((ms) => ms.map((x) => (x.id === m.id ? { ...x, ai_candidate: null } : x)));
    try { await api.post(`/media/${m.id}/dismiss-candidate`); } catch (e) {}
  };

  const [addPicker, setAddPicker] = useState(null); // { section, candidates }
  const [addQuery, setAddQuery] = useState("");
  const addCustomItem = async (sec) => {
    setAddQuery("");
    setAddPicker({ section: sec, candidates: [] });
    try {
      const { data } = await api.get(`/inspections/${id}/item-library`, { params: { room: sec.title } });
      setAddPicker({ section: sec, candidates: data.candidates || [] });
    } catch (e) { setAddPicker({ section: sec, candidates: [] }); }
  };

  const addLibraryItem = async (sec, cand) => {
    try {
      const { data } = await api.post(`/inspections/${id}/items`, {
        room: sec.title, label: cand.label, lib_key: cand.lib_key, section_key: sec.key,
      });
      setResponses((r) => ({ ...r, [data.item_key]: data.entry }));
      toast.success(`Added "${data.entry.label}"`);
      setAddPicker(null);
    } catch (e) { toast.error("Could not add item"); }
  };

  const addFreeTextItem = async (sec) => {
    const label = (addQuery || "").trim();
    if (!label) return;
    try {
      const { data } = await api.post(`/inspections/${id}/items`, { room: sec.title, label, section_key: sec.key });
      setResponses((r) => ({ ...r, [data.item_key]: data.entry }));
      toast.success(`Added "${data.entry.label}"`);
      setAddPicker(null);
    } catch (e) { toast.error("Could not add item"); }
  };

  const removeCustomItem = async (key) => {
    setResponses((r) => { const nr = { ...r }; delete nr[key]; return nr; });
    const attached = media.filter((m) => m.item_key === key);
    setMedia((ms) => ms.map((m) => (m.item_key === key ? { ...m, item_key: null, auto_assigned: false } : m)));
    for (const m of attached) { try { await api.post(`/media/${m.id}/assign`, { item_key: null }); } catch (e) {} }
  };

  const closeWalkthrough = () => {
    setWalk(false);
    if (online && navigator.onLine !== false && media.some((m) => !m.item_key)) autoMatch();
  };

  const aiCheck = async (key, room, mediaId) => {
    toast.loading("Re-analysing all photos on this item…", { id: "ai" });
    try {
      const { data } = await api.post(`/inspections/${id}/items/${encodeURIComponent(key)}/analyze-item`, { room: room || "" });
      toast.dismiss("ai");
      if (!data || !data.photo_count) { toast.message("No photos on this item yet"); return; }
      const patch = { ...(data.condition || {}) };
      if (data.description) { patch.note = data.description; patch.ai_generated = true; }
      setItem(key, room, patch);
      toast.success(`AI (${data.photo_count} photo${data.photo_count > 1 ? "s" : ""}): ${data.description}`, { duration: 5000 });
    } catch (e) { toast.dismiss("ai"); toast.error("Analysis unavailable"); }
  };

  const micNote = (it, room) => {
    const cur = responses[it.key]?.note || "";
    if (dict.listening === it.key) dict.stop();
    else dict.start(it.key, cur, (txt) => setItem(it.key, room, { note: txt }, true));
  };
  const micTranscript = () => {
    if (dict.listening === "transcript") dict.stop();
    else dict.start("transcript", transcript, (txt) => setTranscript(txt));
  };

  const [gapWarn, setGapWarn] = useState(null);
  const [checking, setChecking] = useState(false);

  const runGenerate = async () => {
    setGapWarn(null);
    setGen(true);
    setGenStep(0);
    const steps = [700, 900, 1100];
    steps.forEach((_, i) => setTimeout(() => setGenStep(i + 1), steps.slice(0, i + 1).reduce((a, b) => a + b, 0)));
    try {
      const { data } = await api.post(`/inspections/${id}/generate-report`);
      setGenStep(4);
      setTimeout(() => nav(`/reports/${data.id}/review`), 600);
    } catch (e) {
      setGen(false);
      toast.error("Report generation failed");
    }
  };

  const generate = async () => {
    await save(true);
    setChecking(true);
    try {
      const { data } = await api.get(`/inspections/${id}/coverage`);
      if ((data.empty_rooms?.length || 0) > 0 || (data.exit_gaps?.length || 0) > 0 || (data.rooms_without_overall?.length || 0) > 0) {
        setGapWarn(data);
        setChecking(false);
        return;
      }
    } catch (e) { /* non-blocking: proceed if the check itself fails */ }
    setChecking(false);
    await runGenerate();
  };

  if (!insp) return <div className="space-y-4"><Skeleton className="h-8 w-40" /><Skeleton className="h-96" /></div>;

  const schema = insp.schema;
  const section = schema.sections[activeRoom];
  const totalItems = schema.sections.reduce((a, s) => a + s.items.length, 0);

  const assessed = schema.sections.reduce(
    (a, s) => a + s.items.filter((it) => responses[it.key] && Object.keys(RATING_LABEL).some((f) => responses[it.key][f] !== undefined)).length, 0
  );
  const photos = media.length;
  const issues = Object.values(responses).filter((r) => Object.keys(RATING_LABEL).some((f) => r[f] === false)).length;
  const coverage = totalItems ? Math.round((assessed / totalItems) * 100) : 0;
  const roomOverview = media.filter((m) => m.room === section.title && !m.item_key && m.shot_type === "overall");
  const roomUnassigned = media.filter((m) => m.room === section.title && !m.item_key && m.shot_type !== "overall");

  return (
    <div>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="photo-input" />

      <AnimatePresence>
        {cam && (
          <CameraCapture open={!!cam} target={{ label: cam.label }} onClose={() => setCam(null)}
            onCapture={(dataUrl) => uploadPhoto(cam.key, cam.room, dataUrl)} />
        )}
        {walk && (
          <WalkthroughCapture open={walk} inspectionId={id} sections={schema.sections} piiScan={piiScan} online={online}
            guided={guided} startRoom={activeRoom} onRoomChange={setActiveRoom}
            onClose={closeWalkthrough}
            onCaptured={(m) => setMedia((ms) => [...ms, m])}
            onAssign={(itemKey, room, analysis, byVoice) => { applyAnalysis(itemKey, room, analysis); if (!byVoice) analyzeItem(itemKey, room); }}
            patchMedia={patchMedia} />
        )}
      </AnimatePresence>

      <div className="flex items-center justify-between mb-4 gap-2">
        <button onClick={() => nav(-1)} className="flex items-center gap-1 text-sm text-secondary shrink-0"><ChevronLeft size={16} /> Back</button>
        <div className="flex items-center gap-1.5 flex-wrap justify-end">
          <button data-testid="toggle-online" onClick={() => setOnline((o) => !o)} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border" style={{ borderColor: "var(--border)", color: online ? "var(--success)" : "var(--warning)" }}>
            {online ? <Wifi size={13} /> : <WifiOff size={13} />} {online ? "Online" : "Offline"}
          </button>
          <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border" style={{ borderColor: "var(--border)", color: "var(--text-secondary)" }}>
            <Cloud size={13} /> {pending > 0 ? `${pending} queued` : "Synced"}
          </span>
          <button data-testid="toggle-pii" onClick={() => setPiiScan((v) => !v)} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border" style={{ borderColor: "var(--border)", color: piiScan ? "var(--accent)" : "var(--text-secondary)", background: piiScan ? "var(--accent-soft)" : "transparent" }}>
            <ShieldCheck size={13} /> PII {piiScan ? "on" : "off"}
          </button>
        </div>
      </div>

      <div className="mb-4">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ color: "var(--text)" }}>{insp.property?.address}</h1>
        <div className="flex items-center gap-2 flex-wrap mt-1">
          <p className="text-sm text-secondary capitalize">{schema.type_label} · {schema.state} · schema v{schema.version}</p>
          {onsite?.verified && (
            <Pill tone="success" data-testid="onsite-badge" title={`Device ${onsite.distance_m}m from the property address`}>
              <MapPin size={11} /> On-site verified
            </Pill>
          )}
          {onsite && !onsite.verified && (
            <Pill tone="warning" data-testid="onsite-badge" title={`Device ${onsite.distance_m}m away — outside the ${onsite.radius_m}m radius`}>
              <MapPin size={11} /> Location not verified
            </Pill>
          )}
        </div>
      </div>

      {/* Guided / Advanced mode toggle */}
      <div className="inline-flex p-1 rounded-full border mb-4" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="mode-toggle">
        {[["guided", "Guided", true], ["advanced", "Advanced", false]].map(([id, label, g]) => (
          <button key={id} data-testid={`mode-${id}`} onClick={() => setGuided(g)}
            className="px-4 py-1.5 rounded-full text-xs font-semibold transition-colors"
            style={{ background: guided === g ? "var(--accent)" : "transparent", color: guided === g ? "var(--accent-fg)" : "var(--text-secondary)" }}>
            {label}
          </button>
        ))}
      </div>

      {/* Walkthrough CTA (advanced only — guided has per-room capture) */}
      {!guided && (
        <button data-testid="start-walkthrough" onClick={() => setWalk(true)}
          className="w-full mb-4 flex items-center gap-3 p-4 rounded-2xl text-left transition-transform active:scale-[0.99]"
          style={{ background: "var(--accent)", color: "var(--accent-fg)" }}>
          <div className="h-11 w-11 rounded-xl grid place-items-center shrink-0" style={{ background: "rgba(127,127,127,0.28)" }}>
            <Footprints size={22} />
          </div>
          <div className="min-w-0">
            <div className="font-semibold">Start walkthrough</div>
            <div className="text-xs" style={{ opacity: 0.75 }}>Walk, talk & shoot — capture room-by-room with live voice commentary</div>
          </div>
        </button>
      )}

      {/* Live coverage strip */}
      <Surface className="p-4 mb-5" data-testid="coverage-strip">
        <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
          <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: "var(--text)" }}>
            <ClipboardCheck size={15} className="accent-text" /> Live coverage
          </div>
          <div className="flex items-center gap-3 text-xs text-secondary flex-wrap">
            <span data-testid="coverage-assessed">{assessed}/{totalItems} items</span>
            <span className="flex items-center gap-1" data-testid="coverage-photos"><Camera size={12} /> {photos}</span>
            {issues > 0 && <Pill tone="danger" data-testid="coverage-issues"><AlertTriangle size={11} /> {issues} flagged</Pill>}
          </div>
        </div>
        <div className="h-2.5 rounded-full overflow-hidden" style={{ background: "var(--surface-2)" }}>
          <motion.div className="h-full rounded-full" style={{ background: "var(--accent)" }}
            initial={{ width: 0 }} animate={{ width: `${coverage}%` }} transition={{ type: "spring", stiffness: 120, damping: 20 }}
            data-testid="coverage-bar" />
        </div>
        <div className="mt-3">
          <AButton variant="text" testid="assume-all-good" onClick={() => { markGood(schema.sections); toast.success("All items marked good — flag any exceptions"); }}>
            <CheckCheck size={15} className="inline mr-1" /> Assume all good (whole property)
          </AButton>
        </div>
      </Surface>

      {guided && (
        <GuidedInspection schema={schema} roomIdx={activeRoom} setRoomIdx={setActiveRoom}
          responses={responses} media={media} dict={dict}
          h={{
            setWalk, assignMedia, matching, autoMatch, acceptCandidate, dismissCandidate,
            setItem, setCam, onPickFile, unassignMedia,
            aiCheck, addCustomItem, removeCustomItem, checking, generate,
            markGoodRoom: (sec) => { markGood([sec]); toast.success(`${sec.title}: all good`); },
          }} />
      )}

      {!guided && (<>
      <div className="flex flex-col lg:grid lg:grid-cols-[220px_1fr] gap-5">
        {/* Room nav */}
        <div className="lg:sticky lg:top-24 lg:self-start min-w-0">
          <div className="flex lg:flex-col gap-1.5 overflow-x-auto pb-2 lg:pb-0 w-full min-w-0">
            {schema.sections.map((s, i) => {
              const done = s.items.filter((it) => responses[it.key]).length;
              return (
                <button key={s.key} data-testid={`room-tab-${i}`} onClick={() => setActiveRoom(i)}
                  className="shrink-0 lg:w-full flex items-center justify-between gap-2 px-3.5 py-2.5 rounded-xl text-sm font-medium text-left transition-colors"
                  style={{ background: activeRoom === i ? "var(--accent-soft)" : "var(--surface)", color: activeRoom === i ? "var(--accent)" : "var(--text)", border: "1px solid var(--border)" }}>
                  <span className="truncate">{s.title}</span>
                  {done > 0 && <span className="text-xs shrink-0" style={{ color: "var(--success)" }}>{done}</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Items */}
        <div className="space-y-3 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h2 className="text-base font-semibold" style={{ color: "var(--text)" }}>{section.title}</h2>
            <div className="flex items-center gap-3">
              <AButton variant="text" testid="add-item-btn" onClick={() => addCustomItem(section)}>
                <Plus size={14} className="inline mr-1" /> Add item
              </AButton>
              <AButton variant="text" testid="mark-room-good" onClick={() => { markGood([section]); toast.success(`${section.title}: all good`); }}>
                <CheckCheck size={14} className="inline mr-1" /> Mark room good
              </AButton>
            </div>
          </div>

          {/* Room overview — wide/establishing shots (room-level context, not item evidence) */}
          <RoomOverview roomOverview={roomOverview} section={section} assignMedia={assignMedia} />

          {/* Room evidence pool (unassigned walkthrough photos) */}
          <RoomEvidencePool roomUnassigned={roomUnassigned} section={section} matching={matching} autoMatch={autoMatch}
            acceptCandidate={acceptCandidate} dismissCandidate={dismissCandidate} assignMedia={assignMedia} />

          {section.items.map((it) => (
            <ItemCard key={it.key} it={it} r={responses[it.key] || {}} itemMedia={media.filter((m) => m.item_key === it.key)}
              room={section.title} dict={dict} setItem={setItem} setCam={setCam} onPickFile={onPickFile}
              unassignMedia={unassignMedia} aiCheck={aiCheck} assignMedia={assignMedia} />
          ))}

          {/* Ad-hoc items added on-site (AI-suggested or manual) */}
          {Object.entries(responses)
            .filter(([, v]) => v && v.custom && (v.section_key === section.key || v.room === section.title))
            .map(([ckey, cr]) => (
              <CustomItemCard key={ckey} ckey={ckey} cr={cr} itemMedia={media.filter((m) => m.item_key === ckey)}
                room={section.title} dict={dict} setItem={setItem} setCam={setCam} removeCustomItem={removeCustomItem} />
            ))}
        </div>
      </div>

      {/* Voice commentary */}
      <Surface className="p-4 mt-5">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 text-sm font-medium text-secondary"><Mic size={15} /> Voice commentary transcript</div>
          {dict.supported && (
            <button data-testid="dictate-transcript" onClick={micTranscript}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors"
              style={{ borderColor: dict.listening === "transcript" ? "var(--accent)" : "var(--border)", background: dict.listening === "transcript" ? "var(--accent)" : "transparent", color: dict.listening === "transcript" ? "#fff" : "var(--text-secondary)" }}>
              {dict.listening === "transcript" ? <><MicOff size={13} /> Stop</> : <><Mic size={13} /> Dictate</>}
            </button>
          )}
        </div>
        <textarea value={transcript} onChange={(e) => setTranscript(e.target.value)} rows={3} data-testid="transcript-input"
          placeholder="e.g. Moving into the kitchen now, walls are clean but there's a small crack above the rangehood…"
          className="mt-2 w-full text-sm rounded-lg px-3 py-2 outline-none border"
          style={{ background: "var(--surface-2)", borderColor: dict.listening === "transcript" ? "var(--accent)" : "var(--border)", color: "var(--text)" }} />
      </Surface>

      <div className="sticky bottom-24 lg:bottom-4 mt-6 flex justify-between items-center gap-3 p-3 rounded-2xl glass border" style={{ borderColor: "var(--border)" }}>
        <AButton variant="text" testid="save-progress" onClick={() => save(false)}>Save progress</AButton>
        <AButton testid="generate-report-btn" disabled={checking} onClick={generate}>
          {checking ? <Loader2 size={16} className="inline mr-1.5 animate-spin" /> : <Sparkles size={16} className="inline mr-1.5" />}
          {checking ? "Checking coverage…" : "Generate AI report"}
        </AButton>
      </div>
      </>
      )}

      <AnimatePresence>
        {gen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 grid place-items-center p-6" style={{ background: "rgba(0,0,0,0.4)", backdropFilter: "blur(8px)" }}>
            <motion.div initial={{ scale: 0.9, y: 10 }} animate={{ scale: 1, y: 0 }}>
              <Surface className="p-8 w-[340px] text-center">
                <div className="h-14 w-14 rounded-2xl mx-auto grid place-items-center" style={{ background: "var(--accent-soft)" }}>
                  <Sparkles size={26} className="accent-text" />
                </div>
                <div className="mt-4 text-lg font-semibold" style={{ color: "var(--text)" }}>Drafting your report</div>
                <div className="mt-5 space-y-3 text-left">
                  {["Validating evidence & media hashes", "Transcribing voice commentary", "Vision analysis (GPT-5.5)", "Compiling report (Claude Sonnet 4.6)"].map((s, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm">
                      {genStep > i ? <Check size={16} style={{ color: "var(--success)" }} /> :
                        genStep === i ? <Loader2 size={16} className="animate-spin accent-text" /> :
                        <div className="h-4 w-4 rounded-full border" style={{ borderColor: "var(--border)" }} />}
                      <span style={{ color: genStep >= i ? "var(--text)" : "var(--text-secondary)" }}>{s}</span>
                    </div>
                  ))}
                </div>
              </Surface>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Dialog open={!!addPicker} onOpenChange={(o) => !o && setAddPicker(null)}>
        <DialogContent style={{ background: "var(--surface)", borderColor: "var(--border)" }} data-testid="add-item-dialog">
          <DialogHeader>
            <DialogTitle style={{ color: "var(--text)" }}>Add an item to {addPicker?.section?.title}</DialogTitle>
          </DialogHeader>
          <DialogDescription className="text-sm text-secondary">Pick from the standard library (keeps items consistent across inspections) or type your own.</DialogDescription>
          <input autoFocus value={addQuery} onChange={(e) => setAddQuery(e.target.value)} data-testid="add-item-search"
            placeholder="Search items (e.g. air conditioner, dishwasher)…"
            className="mt-3 w-full text-sm rounded-lg px-3 py-2 outline-none border"
            style={{ background: "var(--surface-2)", borderColor: "var(--border)", color: "var(--text)" }} />
          <div className="mt-3 max-h-64 overflow-auto space-y-1" data-testid="add-item-list">
            {(addPicker?.candidates || [])
              .filter((c) => {
                const q = addQuery.trim().toLowerCase();
                if (!q) return true;
                return c.label.toLowerCase().includes(q) || (c.aliases || []).some((a) => a.toLowerCase().includes(q));
              })
              .slice(0, 40)
              .map((c) => (
                <button key={c.lib_key} data-testid={`add-item-${c.lib_key}`} onClick={() => addLibraryItem(addPicker.section, c)}
                  className="w-full flex items-center gap-2 text-left px-3 py-2 rounded-lg text-sm border transition-colors hover:border-[var(--accent)]"
                  style={{ background: "var(--surface-2)", borderColor: "var(--border)", color: "var(--text)" }}>
                  <Plus size={14} className="accent-text shrink-0" /> {c.label}
                </button>
              ))}
          </div>
          {addQuery.trim() && (
            <button data-testid="add-item-custom" onClick={() => addFreeTextItem(addPicker.section)}
              className="mt-3 w-full py-2 rounded-lg text-sm font-semibold text-white" style={{ background: "var(--accent)" }}>
              + Add "{addQuery.trim()}" as a custom item
            </button>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!gapWarn} onOpenChange={(o) => !o && setGapWarn(null)}>
        <DialogContent style={{ background: "var(--surface)", borderColor: "var(--border)" }} data-testid="coverage-gap-dialog">
          <DialogHeader>
            <DialogTitle style={{ color: "var(--text)" }}>
              <span className="flex items-center gap-2"><AlertTriangle size={18} style={{ color: "var(--warning)" }} /> Documentation gaps found</span>
            </DialogTitle>
          </DialogHeader>
          <DialogDescription className="text-sm text-secondary">You can still generate the report — you always have the final say — but these areas look under-documented:</DialogDescription>
          {gapWarn?.empty_rooms?.length > 0 && (
            <div className="mt-3" data-testid="gap-empty-rooms">
              <div className="text-xs font-semibold uppercase tracking-wide text-secondary mb-1.5">Rooms with no photos</div>
              <div className="flex flex-wrap gap-1.5">
                {gapWarn.empty_rooms.map((r) => <Pill key={r} tone="warning">{r}</Pill>)}
              </div>
            </div>
          )}
          {gapWarn?.exit_gaps?.length > 0 && (
            <div className="mt-4" data-testid="gap-exit-items">
              <div className="text-xs font-semibold uppercase tracking-wide text-secondary mb-1.5">Baseline items not re-inspected at exit ({gapWarn.exit_gaps.length})</div>
              <div className="max-h-40 overflow-auto space-y-1.5">
                {gapWarn.exit_gaps.map((g) => (
                  <div key={g.item_key} className="text-sm flex items-center gap-2">
                    <Pill tone="danger">{g.room}</Pill>
                    <span style={{ color: "var(--text)" }}>{g.label}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-secondary mt-2">These had photos at entry but none at exit — re-inspecting them protects any bond claim.</p>
            </div>
          )}
          {gapWarn?.rooms_without_overall?.length > 0 && (
            <div className="mt-4" data-testid="gap-no-overall">
              <div className="text-xs font-semibold uppercase tracking-wide text-secondary mb-1.5">No wide / overall shot</div>
              <div className="flex flex-wrap gap-1.5">
                {gapWarn.rooms_without_overall.map((r) => <Pill key={r} tone="neutral">{r}</Pill>)}
              </div>
              <p className="text-xs text-secondary mt-2">These rooms have close-ups but no establishing shot — a wide view proves context and scale at tribunal.</p>
            </div>
          )}
          <div className="flex gap-2 mt-5">
            <AButton testid="gap-keep-capturing" variant="text" className="flex-1" onClick={() => setGapWarn(null)}>Keep capturing</AButton>
            <AButton testid="gap-generate-anyway" className="flex-1" onClick={runGenerate}>Generate anyway</AButton>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
